import { AxiosResponse } from 'axios'
import ApiService from '@/core/services/ApiService'
import {
  AdminOrder,
  AuditEntry,
  BuyerRequisites,
  Filters,
  Incident,
  IncidentResolution,
  LaravelPage,
  Paginated,
  PayerType,
  PaymentDashboard,
  PaymentDocument,
  PaymentRow,
  Product,
  ProductPayload,
  UserAccess,
} from './types'

/**
 * Единственное место, где фронтенд знает про URL и формат ответов админского API платежей.
 * baseURL (VUE_APP_HOST_API) уже содержит /api/v1, поэтому пути начинаются с /admin/payments.
 * Используются только GET, POST и PATCH.
 */
const BASE = '/admin/payments'

/**
 * Готовит query-параметры: убирает пустые значения, а флаги переводит в 1/0
 * (правило Laravel boolean не принимает строки 'true' и 'false').
 */
export const cleanParams = (params: Filters): Filters => {
  const result: Filters = {}
  Object.keys(params).forEach(key => {
    const value = params[key]
    const empty =
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    if (!empty) {
      result[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value
    }
  })
  return result
}

const data = <T>(response: AxiosResponse): T =>
  (response.data?.data ?? response.data) as T

const page = <T>(response: AxiosResponse<LaravelPage<T>>): Paginated<T> => ({
  items: response.data.items,
  page: response.data.meta.page,
  pages: response.data.meta.pages,
  perPage: response.data.meta.limit,
  total: response.data.meta.total,
})

const list = <T>(path: string, filters: Filters): Promise<Paginated<T>> =>
  ApiService.query(`${BASE}/${path}`, { params: cleanParams(filters) }).then(
    r => page<T>(r)
  )

export const PaymentAdminApi = {
  /** Сводка для главной страницы админки. */
  dashboard: {
    get: () =>
      ApiService.get(`${BASE}/dashboard`).then(r => data<PaymentDashboard>(r)),
  },

  orders: {
    list: (filters: Filters) => list<AdminOrder>('orders', filters),

    get: (id: string) =>
      ApiService.get(`${BASE}/orders/${id}`).then(r => data<AdminOrder>(r)),

    /** Заказ от имени пользователя (юрлицу сразу выставляется счёт). */
    create: (payload: {
      user_id: number
      product_type: string
      unit?: string
      unit_count?: number
      model_id?: number
      payer_type: PayerType
      buyer?: BuyerRequisites
      due_in_days?: number
    }) =>
      ApiService.post(`${BASE}/orders`, payload).then(r => ({
        order: data<AdminOrder>(r),
        buyerSource: r.data.buyer_source as string | null,
      })),

    cancel: (id: string) =>
      ApiService.post(`${BASE}/orders/${id}/cancel`, {}).then(r =>
        data<AdminOrder>(r)
      ),

    /** Сверка висящих платежей со шлюзом. */
    refresh: (id: string) =>
      ApiService.post(`${BASE}/orders/${id}/refresh`, {}).then(r => ({
        order: data<AdminOrder>(r),
        hasPendingPayment: Boolean(r.data.has_pending_payment),
      })),

    retryActivation: (id: string) =>
      ApiService.post(`${BASE}/orders/${id}/retry-activation`, {}).then(r =>
        data<AdminOrder>(r)
      ),

    markActivated: (id: string, reason: string) =>
      ApiService.post(`${BASE}/orders/${id}/mark-activated`, { reason }).then(
        r => data<AdminOrder>(r)
      ),

    /** Фиксация ручного возврата (деньги уже возвращены в кабинете ЮKassa). */
    refund: (
      id: string,
      payload: { amount?: number; reference?: string; note?: string }
    ) =>
      ApiService.post(`${BASE}/orders/${id}/refund`, payload).then(r => ({
        order: data<AdminOrder>(r),
        notRevokedItems: (r.data.not_revoked_items || []) as unknown[],
      })),

    createInvoice: (
      id: string,
      payload: { buyer?: BuyerRequisites; due_in_days?: number }
    ) =>
      ApiService.post(`${BASE}/orders/${id}/invoice`, payload).then(r =>
        data<PaymentDocument>(r)
      ),
  },

  payments: {
    list: (filters: Filters) => list<PaymentRow>('payments', filters),

    /** Подтверждение оплаты счёта по выписке. Повтор с тем же reference идемпотентен. */
    confirmInvoice: (payload: {
      number: string
      paid_at?: string | null
      reference?: string | null
    }) =>
      ApiService.post(`${BASE}/invoices/confirm-payment`, cleanParams(payload)).then(
        r => ({
          order: data<AdminOrder>(r),
          paymentId: r.data.payment_id as string,
        })
      ),
  },

  documents: {
    list: (filters: Filters) => list<PaymentDocument>('documents', filters),

    get: (id: string) =>
      ApiService.get(`${BASE}/documents/${id}`).then(r =>
        data<PaymentDocument>(r)
      ),

    /** PDF как Blob: ссылка требует Authorization, поэтому обычный <a href> не работает. */
    pdf: (id: string): Promise<Blob> =>
      ApiService.getBlob(`${BASE}/documents/${id}/pdf`),

    cancel: (id: string) =>
      ApiService.post(`${BASE}/documents/${id}/cancel`, {}).then(r =>
        data<PaymentDocument>(r)
      ),

    issueAct: (invoiceId: string) =>
      ApiService.post(`${BASE}/documents/${invoiceId}/act`, {}).then(r =>
        data<PaymentDocument>(r)
      ),

    regeneratePdf: (id: string) =>
      ApiService.post(`${BASE}/documents/${id}/regenerate-pdf`, {}).then(r =>
        data<PaymentDocument>(r)
      ),
  },

  incidents: {
    list: (filters: Filters) => list<Incident>('incidents', filters),

    resolve: (id: string, resolution: IncidentResolution, note?: string) =>
      ApiService.post(`${BASE}/incidents/${id}/resolve`, cleanParams({ resolution, note })).then(
        r => data<Incident>(r)
      ),
  },

  products: {
    list: (filters: Filters) => list<Product>('products', filters),

    create: (payload: ProductPayload) =>
      ApiService.post(`${BASE}/products`, payload).then(r => data<Product>(r)),

    update: (id: string, payload: ProductPayload) =>
      ApiService.patch(`${BASE}/products/${id}`, payload).then(r =>
        data<Product>(r)
      ),

    archive: (id: string) =>
      ApiService.post(`${BASE}/products/${id}/archive`, {}).then(r =>
        data<Product>(r)
      ),
  },

  access: {
    get: (userId: number | string) =>
      ApiService.get(`${BASE}/users/${userId}/access`).then(r =>
        data<UserAccess>(r)
      ),

    grantPeriod: (
      userId: number | string,
      payload: { unit: 'day' | 'month'; count: number; reason: string }
    ) => ApiService.post(`${BASE}/users/${userId}/access-periods`, payload),

    revokePeriod: (periodId: string, reason: string) =>
      ApiService.post(`${BASE}/access-periods/${periodId}/revoke`, { reason }),

    grantPass: (
      userId: number | string,
      payload: { model_id: number; reason: string }
    ) => ApiService.post(`${BASE}/users/${userId}/passes`, payload),

    revokePass: (passId: string, reason: string) =>
      ApiService.post(`${BASE}/passes/${passId}/revoke`, { reason }),

    changeQuota: (
      userId: number | string,
      payload: { delta: number; reason: string }
    ) => ApiService.post(`${BASE}/users/${userId}/quota`, payload),
  },

  audit: {
    list: (filters: Filters) => list<AuditEntry>('audit', filters),
  },
}

export default PaymentAdminApi
